// Pure Records URL model (architecture sprint-05 §6.2). The URL is the only state: ?from&to&user&
// project&sort&dir&page&size. Invalid values fall back to defaults and the page replaces the URL.
import { addDays, type LocalDate } from '@shared/calendar';
import { RECORDS_DEFAULT_DAYS, RECORDS_DEFAULT_PAGE_SIZE, RECORDS_MAX_PAGE, RECORDS_PAGE_SIZES } from '@shared/report-rules';
import { isValidReportRange } from '@/lib/report-model';
import type { RecordSort, SortDirection } from '@/types/admin';

export const RECORD_SORTS: readonly RecordSort[] = ['date', 'user', 'project', 'description', 'start', 'end', 'hours'];
/** Numeric and time columns start descending; text columns start ascending. */
const FIRST_CLICK_DIR: Record<RecordSort, SortDirection> = {
  date: 'desc',
  user: 'asc',
  project: 'asc',
  description: 'asc',
  start: 'desc',
  end: 'desc',
  hours: 'desc',
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL = 'all';
const NONE = 'none';

export interface RecordsParams {
  from: LocalDate;
  to: LocalDate;
  /** 'all' or a user id. */
  user: string;
  /** 'all', 'none' or a project id. */
  project: string;
  sort: RecordSort;
  dir: SortDirection;
  page: number;
  size: number;
}

export function defaultRecordsParams(today: LocalDate): RecordsParams {
  return {
    from: addDays(today, 1 - RECORDS_DEFAULT_DAYS),
    to: today,
    user: ALL,
    project: ALL,
    sort: 'date',
    dir: 'desc',
    page: 1,
    size: RECORDS_DEFAULT_PAGE_SIZE,
  };
}

export interface KnownOptions {
  /** undefined while the list is loading: any well-formed id is accepted until then. */
  userIds?: ReadonlySet<string>;
  projectIds?: ReadonlySet<string>;
}

function isSort(v: string | null): v is RecordSort {
  return v !== null && (RECORD_SORTS as readonly string[]).includes(v);
}

function positiveInt(v: string | null): number | null {
  if (v === null || !/^\d+$/.test(v)) return null;
  const n = Number(v);
  return n >= 1 && n <= RECORDS_MAX_PAGE ? n : null;
}

/** Parsed params plus whether the URL already held exactly these values (else the page replaces it). */
export function parseRecordsParams(search: URLSearchParams, today: LocalDate, known: KnownOptions = {}): { params: RecordsParams; canonical: boolean } {
  const defaults = defaultRecordsParams(today);
  const rawFrom = search.get('from');
  const rawTo = search.get('to');
  const rangeOk = isValidReportRange(rawFrom, rawTo);

  const rawUser = search.get('user');
  const userOk = rawUser === null || rawUser === ALL || (UUID.test(rawUser) && (known.userIds?.has(rawUser) ?? true));
  const rawProject = search.get('project');
  const projectOk =
    rawProject === null || rawProject === ALL || rawProject === NONE || (UUID.test(rawProject) && (known.projectIds?.has(rawProject) ?? true));

  const rawSort = search.get('sort');
  const rawDir = search.get('dir');
  const rawSize = search.get('size');
  const size = rawSize !== null && (RECORDS_PAGE_SIZES as readonly number[]).includes(Number(rawSize)) ? Number(rawSize) : null;
  const page = positiveInt(search.get('page'));

  const params: RecordsParams = {
    from: rangeOk && rawFrom ? rawFrom : defaults.from,
    to: rangeOk && rawTo ? rawTo : defaults.to,
    user: userOk && rawUser !== null ? rawUser : ALL,
    project: projectOk && rawProject !== null ? rawProject : ALL,
    sort: isSort(rawSort) ? rawSort : defaults.sort,
    dir: rawDir === 'asc' || rawDir === 'desc' ? rawDir : defaults.dir,
    page: page ?? 1,
    size: size ?? defaults.size,
  };
  const canonical = recordsSearch(params).toString() === search.toString();
  return { params, canonical };
}

/** The page URL, in a fixed key order. */
export function recordsSearch(p: RecordsParams): URLSearchParams {
  return new URLSearchParams({
    from: p.from,
    to: p.to,
    user: p.user,
    project: p.project,
    sort: p.sort,
    dir: p.dir,
    page: String(p.page),
    size: String(p.size),
  });
}

function filterQuery(p: RecordsParams): URLSearchParams {
  const q = new URLSearchParams({ from: p.from, to: p.to });
  if (p.user !== ALL) q.set('userId', p.user);
  if (p.project !== ALL) q.set('projectId', p.project);
  q.set('sort', p.sort);
  q.set('dir', p.dir);
  return q;
}

/** GET /api/admin/records query. */
export function recordsApiQuery(p: RecordsParams): string {
  const q = filterQuery(p);
  q.set('page', String(p.page));
  q.set('pageSize', String(p.size));
  return q.toString();
}

/** GET /api/admin/records/export path: the same filters and sort, plus the format. */
export function recordsExportPath(p: RecordsParams, format: 'csv' | 'xlsx'): string {
  const q = filterQuery(p);
  q.set('format', format);
  return `/api/admin/records/export?${q.toString()}`;
}

/** Header click: a new column starts in its natural direction; the same column flips. */
export function nextSort(current: { sort: RecordSort; dir: SortDirection }, clicked: RecordSort): { sort: RecordSort; dir: SortDirection } {
  if (current.sort === clicked) return { sort: clicked, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { sort: clicked, dir: FIRST_CLICK_DIR[clicked] };
}

export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}

/** Sprint 11 (§8.6): the oversight records query. The project comes from the path, so no projectId key. */
export function projectRecordsApiQuery(p: RecordsParams): string {
  return recordsApiQuery({ ...p, project: ALL });
}

/** Sprint 11: GET /api/oversight/projects/:projectId/records/export. */
export function projectRecordsExportPath(projectId: string, p: RecordsParams, format: 'csv' | 'xlsx'): string {
  const q = filterQuery({ ...p, project: ALL });
  q.set('format', format);
  return `/api/oversight/projects/${projectId}/records/export?${q.toString()}`;
}
